import React, { useState, useEffect, useCallback } from 'react';
import { generateQuestionnaireAnswers } from '../../utils/questionnaireAI';
import { DataManager } from '../../services/dataManager';
import html2pdf from 'html2pdf.js';

/**
 * Questionario guidato per la sintesi del cliente
 * Sostituisce la sintesi a testo libero con domande strutturate
 * pre-compilate dall'AI e confermate dal counselor
 */

// Definizione delle sezioni e domande del questionario
const QUESTIONNAIRE_STRUCTURE = [
    {
        id: 'anamnesis',
        title: '📋 Inquadramento Iniziale',
        icon: '📋',
        color: 'blue',
        questions: [
            { id: 'age', label: 'Età', type: 'text', placeholder: 'Es: 35 anni' },
            { id: 'profession', label: 'Professione/Occupazione', type: 'text', placeholder: 'Es: Insegnante' },
            { id: 'motivation', label: 'Motivazione al percorso', type: 'textarea', placeholder: 'Cosa ha portato la persona in terapia?' },
            { id: 'initialRequest', label: 'Richiesta iniziale', type: 'textarea', placeholder: 'Qual era la richiesta esplicita?' }
        ]
    },
    {
        id: 'emotional',
        title: '💭 Dinamiche Emotive Prevalenti',
        icon: '💭',
        color: 'purple',
        questions: [
            { id: 'dominantEmotion', label: 'Emozione dominante', type: 'text', placeholder: 'Es: Rabbia repressa, Ansia, Tristezza...' },
            { id: 'emotionalPatterns', label: 'Pattern ricorrenti', type: 'textarea', placeholder: 'Quali schemi emotivi si ripetono?' },
            { id: 'emotionalBlocks', label: 'Blocchi emersi', type: 'textarea', placeholder: 'Dove si ferma il flusso emotivo?' }
        ]
    },
    {
        id: 'narrative',
        title: '📖 Temi Narrativi Principali',
        icon: '📖',
        color: 'amber',
        questions: [
            { id: 'centralTheme', label: 'Tema centrale del percorso', type: 'text', placeholder: 'Es: Il confine, L\'abbandono, La performance...' },
            { id: 'subThemes', label: 'Sotto-temi emergenti', type: 'textarea', placeholder: 'Quali altri temi compaiono?' },
            { id: 'narrativeNuclei', label: 'Nuclei narrativi ripetuti', type: 'textarea', placeholder: 'Quali storie/ricordi tornano spesso?' }
        ]
    },
    {
        id: 'progress',
        title: '🌱 Progressi e Consapevolezze',
        icon: '🌱',
        color: 'green',
        questions: [
            { id: 'mainInsights', label: 'Insight principali', type: 'textarea', placeholder: 'Quali consapevolezze sono emerse?' },
            { id: 'growthAreas', label: 'Aree di crescita osservate', type: 'textarea', placeholder: 'Dove si vedono cambiamenti positivi?' },
            { id: 'resistances', label: 'Resistenze osservate', type: 'textarea', placeholder: 'Cosa ostacola ancora il processo?' }
        ]
    },
    {
        id: 'clinical',
        title: '🧠 Quadro del Percorso',
        icon: '🧠',
        color: 'indigo',
        questions: [
            { id: 'characterStructure', label: 'Dinamicizzazione prevalente', type: 'text', placeholder: 'Es: Orale, Masochista, Rigido...' },
            { id: 'transferDynamics', label: 'Dinamica relazionale emergente', type: 'textarea', placeholder: 'Come si manifesta la relazione?' },
            { id: 'clinicalIndications', label: 'Indicazioni per il proseguimento', type: 'textarea', placeholder: 'Su cosa è importante lavorare?' }
        ]
    }
];

const GuidedSynthesisQuestionnaire = ({ client, meetings, works, onClientUpdate }) => {
    const [answers, setAnswers] = useState({});
    const [confirmationStatus, setConfirmationStatus] = useState({});
    const [isGenerating, setIsGenerating] = useState(false);
    const [isEditing, setIsEditing] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [lastGenerationTime, setLastGenerationTime] = useState(0);
    const [isExpanded, setIsExpanded] = useState(false); // Vista compatta/espansa
    const [lastUpdated, setLastUpdated] = useState(null); // Data ultimo aggiornamento

    // Genera sintesi narrativa dalle risposte come elenco puntato
    const generateNarrativeSummary = () => {
        const bullets = [];
        const name = client.name || 'Il cliente';

        // 1. Inquadramento
        if (answers.age || answers.profession) {
            bullets.push(
                <React.Fragment key="profilo">
                    <strong>PROFILO:</strong> {name}{answers.age ? `, ${answers.age}` : ''}{answers.profession ? ` (${answers.profession})` : ''}
                </React.Fragment>
            );
        }
        if (answers.motivation || answers.initialRequest) {
            bullets.push(
                <React.Fragment key="contesto">
                    <strong>CONTESTO:</strong> {answers.motivation || answers.initialRequest}
                </React.Fragment>
            );
        }

        // 2. Emozioni e Corpo
        if (answers.dominantEmotion) {
            bullets.push(
                <React.Fragment key="emozioni">
                    <strong>DINAMICHE EMOTIVE:</strong> {answers.dominantEmotion}{answers.emotionalBlocks ? ` (Blocco: ${answers.emotionalBlocks})` : ''}
                </React.Fragment>
            );
        }

        // 3. Temi (Mappa Semantica)
        if (answers.centralTheme) {
            bullets.push(
                <React.Fragment key="tema">
                    <strong>TEMA PORTANTE:</strong> "{answers.centralTheme}"{answers.subThemes ? ` - ${answers.subThemes}` : ''}
                </React.Fragment>
            );
        }

        // 4. Progressi (Ruota della Vita / Insight)
        if (answers.mainInsights || answers.growthAreas) {
            bullets.push(
                <React.Fragment key="evoluzione">
                    <strong>EVOLUZIONE:</strong> {answers.mainInsights || answers.growthAreas}
                </React.Fragment>
            );
        }

        // 5. Quadro Fenomenologico
        if (answers.characterStructure || answers.clinicalIndications) {
            bullets.push(
                <React.Fragment key="inquadramento">
                    <strong>INQUADRAMENTO STRUTTURALE:</strong> {answers.characterStructure || ''}
                </React.Fragment>
            );
            if (answers.clinicalIndications) {
                bullets.push(
                    <React.Fragment key="indicazioni">
                        <strong>INDICAZIONI FUTURE:</strong> {answers.clinicalIndications}
                    </React.Fragment>
                );
            }
        }

        if (bullets.length === 0) {
            return "Nessuna sintesi disponibile. Genera le risposte per iniziare.";
        }

        return (
            <div className="space-y-4">
                {bullets.map((bullet, index) => (
                    <div key={index}>• {bullet}</div>
                ))}
            </div>
        );
    };

    const handlePrint = () => {
        window.print();
    };

    // Genera sintesi narrativa come array di oggetti {label, text} per il PDF
    const generateNarrativeText = () => {
        const parts = [];
        const name = client.name || 'Il cliente';
        if (answers.age || answers.profession) parts.push({ label: 'PROFILO', text: `${name}${answers.age ? `, ${answers.age}` : ''}${answers.profession ? ` (${answers.profession})` : ''}` });
        if (answers.motivation || answers.initialRequest) parts.push({ label: 'CONTESTO', text: answers.motivation || answers.initialRequest });
        if (answers.dominantEmotion) parts.push({ label: 'DINAMICHE EMOTIVE', text: `${answers.dominantEmotion}${answers.emotionalBlocks ? ` · Blocco: ${answers.emotionalBlocks}` : ''}` });
        if (answers.centralTheme) parts.push({ label: 'TEMA PORTANTE', text: `"${answers.centralTheme}"${answers.subThemes ? ` — ${answers.subThemes}` : ''}` });
        if (answers.mainInsights || answers.growthAreas) parts.push({ label: 'EVOLUZIONE', text: answers.mainInsights || answers.growthAreas });
        if (answers.characterStructure) parts.push({ label: 'INQUADRAMENTO STRUTTURALE', text: answers.characterStructure });
        if (answers.clinicalIndications) parts.push({ label: 'INDICAZIONI FUTURE', text: answers.clinicalIndications });
        return parts;
    };

    const handleExportPDF = async () => {
        setIsSaving(true);
        const reportDate = new Date().toLocaleDateString('it-IT');
        const narrativeParts = generateNarrativeText();
        const sectionColorMap = { blue: '#3b82f6', purple: '#a855f7', amber: '#f59e0b', green: '#22c55e', indigo: '#6366f1' };
        const esc = (str) => str ? String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;') : '';

        const confirmedSections = QUESTIONNAIRE_STRUCTURE.map(section => ({
            ...section,
            confirmedQuestions: section.questions.filter(q => answers[q.id] && (confirmationStatus[q.id] === 'confirmed' || confirmationStatus[q.id] === 'ai-suggested'))
        })).filter(s => s.confirmedQuestions.length > 0);

        const html = `<!DOCTYPE html><html><head><style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&display=swap');
            * { box-sizing: border-box; -webkit-print-color-adjust: exact; }
            body { font-family: 'Inter', sans-serif; color: #1e293b; margin: 0; padding: 0; background: #fff; }
            .page { width: 210mm; padding: 20mm; margin: 0 auto; min-height: 296mm; position: relative; }
            .header { border-bottom: 3px solid #4338ca; padding-bottom: 18px; margin-bottom: 26px; display: flex; justify-content: space-between; align-items: center; }
            h1 { font-size: 22px; font-weight: 800; color: #1e1b4b; margin: 0; text-transform: uppercase; letter-spacing: -0.02em; }
            .subtitle { font-size: 9px; font-weight: 800; color: #6366f1; margin-top: 4px; letter-spacing: 0.2em; text-transform: uppercase; }
            .section-title { font-size: 10px; font-weight: 800; color: #4338ca; text-transform: uppercase; letter-spacing: 0.15em; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 14px; margin-top: 22px; }
            .narrative-block { background: #eef2ff; border-radius: 12px; padding: 18px; }
            .narrative-item { margin-bottom: 10px; font-size: 11px; line-height: 1.6; }
            .narrative-label { font-weight: 800; color: #4338ca; font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; display: block; margin-bottom: 2px; }
            .sec-block { margin-bottom: 16px; padding-left: 14px; border-left: 4px solid #4338ca; }
            .sec-name { font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px; }
            .q-label { font-size: 8px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 3px; }
            .q-answer { font-size: 11px; color: #334155; line-height: 1.6; margin-bottom: 10px; }
            .badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 9px; font-weight: 800; text-transform: uppercase; margin: 3px; }
            .footer { border-top: 1px solid #e2e8f0; padding-top: 10px; margin-top: 30px; font-size: 9px; color: #94a3b8; display: flex; justify-content: space-between; font-weight: 600; }
        </style></head><body><div class="page">
            <div class="header">
                <div><h1>AwarenessTool</h1><p class="subtitle">Report di Supervisione · BioGestalt</p></div>
                <div style="text-align:right">
                    <h2 style="font-size:16px;font-weight:800;margin:0;color:#1e293b">${esc(client.name)}</h2>
                    <p style="font-size:10px;color:#94a3b8;font-weight:600;margin:4px 0 0">Report del ${reportDate}</p>
                    <p style="font-size:9px;color:#94a3b8;font-weight:600;margin:2px 0 0">${confirmedCount}/${totalQuestions} risposte confermate</p>
                </div>
            </div>
            <div class="section-title">1. Sintesi Evolutiva</div>
            <div class="narrative-block">${narrativeParts.length > 0
                ? narrativeParts.map(p => `<div class="narrative-item"><span class="narrative-label">${esc(p.label)}</span>${esc(p.text)}</div>`).join('')
                : '<p style="font-size:11px;color:#94a3b8;font-style:italic">Nessuna sintesi disponibile.</p>'
            }</div>
            ${(answers.dominantEmotion || answers.characterStructure || answers.centralTheme) ? `
            <div class="section-title">2. Indicatori Chiave</div>
            <div>
                ${answers.dominantEmotion ? `<span class="badge" style="background:#f3e8ff;color:#7e22ce">💭 ${esc(answers.dominantEmotion)}</span>` : ''}
                ${answers.characterStructure ? `<span class="badge" style="background:#eef2ff;color:#4338ca">🧠 ${esc(answers.characterStructure)}</span>` : ''}
                ${answers.centralTheme ? `<span class="badge" style="background:#fef3c7;color:#92400e">📖 ${esc(answers.centralTheme)}</span>` : ''}
            </div>` : ''}
            <div class="section-title">${(answers.dominantEmotion || answers.characterStructure || answers.centralTheme) ? '3.' : '2.'} Dettaglio Questionario</div>
            ${confirmedSections.map(section => `
                <div class="sec-block" style="border-left-color:${sectionColorMap[section.color]}">
                    <p class="sec-name" style="color:${sectionColorMap[section.color]}">${esc(section.title)}</p>
                    ${section.confirmedQuestions.map(q => `
                        <p class="q-label">${esc(q.label)}</p>
                        <p class="q-answer">${esc(answers[q.id])}</p>
                    `).join('')}
                </div>`).join('')}
            <div class="footer"><span>CONFIDENZIALE — SOLO PER USO PROFESSIONALE</span><span>AwarenessTool Supervision Engine</span></div>
        </div></body></html>`;

        try {
            const opt = {
                margin: 0,
                filename: `Supervisione_${client.name.replace(/\s+/g,'_')}_${new Date().toISOString().split('T')[0]}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, windowWidth: 794 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };
            const el = document.createElement('div');
            el.innerHTML = html.trim();
            document.body.appendChild(el);
            await html2pdf().from(el).set(opt).save();
            document.body.removeChild(el);
        } catch (e) {
            console.error('PDF Export Error:', e);
            alert('❌ Errore nella generazione del PDF.');
        } finally {
            setIsSaving(false);
        }
    };

    // Carica risposte salvate dal database
    useEffect(() => {
        const loadSavedAnswers = async () => {
            try {
                const savedData = await DataManager.getQuestionnaireAnswers(client.id);
                if (savedData) {
                    setAnswers(savedData.answers || {});
                    setConfirmationStatus(savedData.confirmationStatus || {});
                    setLastUpdated(savedData.lastUpdated || null);
                }
            } catch (error) {
                console.error('Error loading questionnaire:', error);
            }
        };
        loadSavedAnswers();
    }, [client.id]);

    // Genera risposte AI per tutte le domande
    const handleGenerateAll = useCallback(async () => {
        const now = Date.now();
        if (now - lastGenerationTime < 10000) {
            alert('⏳ Per favore, attendi un momento prima di rigenerare (cooldown di sicurezza).');
            return;
        }

        setIsGenerating(true);
        try {
            const clientProfile = {
                name: client.name,
                age: client.age,
                profession: client.profession || client.hobby,
                contract: client.contract
            };

            const generatedAnswers = await generateQuestionnaireAnswers(
                QUESTIONNAIRE_STRUCTURE,
                clientProfile,
                works,
                meetings,
                client.baselineAssessment?.[0]
            );

            // Merge con risposte esistenti (non sovrascrivere confermate)
            const newAnswers = { ...answers };
            const newConfirmation = { ...confirmationStatus };

            Object.entries(generatedAnswers).forEach(([questionId, answer]) => {
                // Non sovrascrivere se già confermato
                if (confirmationStatus[questionId] !== 'confirmed') {
                    newAnswers[questionId] = answer;
                    newConfirmation[questionId] = answer ? 'ai-suggested' : 'missing';
                }
            });

            setAnswers(newAnswers);
            setConfirmationStatus(newConfirmation);
            setLastGenerationTime(now);

            // Salva automaticamente
            await saveToDatabase(newAnswers, newConfirmation);

            // Chiudi vista espansa e mostra sintesi
            setIsExpanded(false);
        } catch (error) {
            console.error('Error generating answers:', error);
            alert('❌ Errore nella generazione delle risposte. Riprova.');
        } finally {
            setIsGenerating(false);
        }
    }, [client, works, meetings, answers, confirmationStatus, lastGenerationTime]);

    // Salva nel database
    const saveToDatabase = async (answersToSave, confirmationToSave) => {
        try {
            // 1. Salva le risposte del questionario
            await DataManager.saveQuestionnaireAnswers(client.id, {
                answers: answersToSave,
                confirmationStatus: confirmationToSave,
                lastUpdated: new Date().toISOString()
            });

            // 2. Sincronizza dati del cliente se necessario (include già il refresh)
            await syncClientData(answersToSave);
        } catch (error) {
            console.error('Error saving questionnaire:', error);
        }
    };

    // Sincronizza i dati del cliente con le risposte del questionario
    const syncClientData = async (answersToSync) => {
        try {
            const updates = {};

            // Età
            if (answersToSync.age) {
                const ageMatch = answersToSync.age.match(/\d+/);
                if (ageMatch) {
                    const newAge = parseInt(ageMatch[0]);
                    if (newAge !== client.age) {
                        updates.age = newAge;
                        console.log('📝 Sincronizzazione età:', newAge);
                    }
                }
            }

            // Professione
            if (answersToSync.profession) {
                const newProfession = answersToSync.profession.replace(/^Es:\s*/i, '').trim();
                if (newProfession && newProfession !== client.profession) {
                    updates.profession = newProfession;
                    console.log('📝 Sincronizzazione professione:', newProfession);
                }
            }

            // Tema contrattuale centrale
            if (answersToSync.centralTheme) {
                const currentTitle = client.contract?.title || '';
                const newTheme = answersToSync.centralTheme.replace(/^Es:\s*/i, '').trim();

                if (newTheme && newTheme !== currentTitle && newTheme !== 'Nuovo Percorso') {
                    updates.contract = {
                        ...client.contract,
                        title: newTheme,
                        description: answersToSync.initialRequest || client.contract?.description || ''
                    };
                    console.log('📝 Sincronizzazione tema:', newTheme);
                }
            }

            // Aggiorna solo se ci sono modifiche
            if (Object.keys(updates).length > 0) {
                console.log('🔄 Aggiornamento cliente in corso...', updates);
                await DataManager.updateClient(client.id, updates);
                console.log('✅ Cliente aggiornato con successo');

                // Mostra notifica di successo
                showSyncNotification(updates);

                // Forza il refresh dei dati
                if (onClientUpdate) {
                    await onClientUpdate();
                }
            } else {
                console.log('ℹ️ Nessuna modifica da sincronizzare');
            }
        } catch (error) {
            console.error('❌ Errore sincronizzazione dati cliente:', error);
        }
    };

    // Mostra notifica di sincronizzazione
    // 🔒 SECURITY: Notifica costruita con DOM sicuro (no innerHTML)
    const showSyncNotification = (updates) => {
        const messages = [];
        if (updates.age) messages.push(`Età: ${updates.age}`);
        if (updates.profession) messages.push(`Professione: ${updates.profession}`);
        if (updates.contract?.title) messages.push(`Tema: ${updates.contract.title}`);

        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50 animate-bounce';

        const wrapper = document.createElement('div');
        wrapper.className = 'flex items-center gap-2';

        const icon = document.createElement('span');
        icon.className = 'text-lg';
        icon.textContent = '✅';

        const textBlock = document.createElement('div');

        const title = document.createElement('div');
        title.className = 'font-bold';
        title.textContent = 'Dati Sincronizzati!';

        const detail = document.createElement('div');
        detail.className = 'text-xs';
        detail.textContent = messages.join(' • ');

        textBlock.appendChild(title);
        textBlock.appendChild(detail);
        wrapper.appendChild(icon);
        wrapper.appendChild(textBlock);
        notification.appendChild(wrapper);

        document.body.appendChild(notification);
        setTimeout(() => notification.remove(), 3000);
    };

    // Conferma una risposta
    const handleConfirm = async (questionId) => {
        const newConfirmation = {
            ...confirmationStatus,
            [questionId]: 'confirmed'
        };
        setConfirmationStatus(newConfirmation);
        await saveToDatabase(answers, newConfirmation);
    };

    // Modifica una risposta
    const handleEdit = (questionId, value) => {
        const newAnswers = {
            ...answers,
            [questionId]: value
        };
        setAnswers(newAnswers);

        // Non ancora confermato se in modifica
        const newConfirmation = {
            ...confirmationStatus,
            [questionId]: 'editing'
        };
        setConfirmationStatus(newConfirmation);
    };

    // Salva modifica
    const handleSaveEdit = async (questionId) => {
        const newConfirmation = {
            ...confirmationStatus,
            [questionId]: 'confirmed'
        };
        setConfirmationStatus(newConfirmation);
        setIsEditing({ ...isEditing, [questionId]: false });
        await saveToDatabase(answers, newConfirmation);
    };

    // Determina lo stato di una risposta
    const getAnswerStatus = (questionId) => {
        const status = confirmationStatus[questionId];
        const hasAnswer = answers[questionId] && answers[questionId].trim();

        if (status === 'confirmed') return { status: 'confirmed', color: 'green', icon: '✅', label: 'Confermato' };
        if (status === 'ai-suggested' && hasAnswer) return { status: 'suggested', color: 'amber', icon: '⚠️', label: 'Ipotesi AI - Da confermare' };
        if (status === 'editing') return { status: 'editing', color: 'blue', icon: '✏️', label: 'In modifica' };
        return { status: 'missing', color: 'red', icon: '❌', label: 'Mancante' };
    };

    // Palette colori sezioni (accent CSS per bordo sinistro + testo header)
    const sectionStyles = {
        blue:   { accent: '#3b82f6', light: '#eff6ff', text: '#1d4ed8' },
        purple: { accent: '#a855f7', light: '#faf5ff', text: '#7e22ce' },
        amber:  { accent: '#f59e0b', light: '#fffbeb', text: '#b45309' },
        green:  { accent: '#22c55e', light: '#f0fdf4', text: '#15803d' },
        indigo: { accent: '#6366f1', light: '#eef2ff', text: '#4338ca' },
    };

    const statusConfig = {
        confirmed: { dot: '#16a34a', badge: 'bg-green-100 text-green-700 border-green-200',  label: 'Confermato' },
        suggested: { dot: '#d97706', badge: 'bg-amber-100 text-amber-700 border-amber-200',  label: 'Da confermare' },
        editing:   { dot: '#2563eb', badge: 'bg-blue-100 text-blue-700 border-blue-200',     label: 'In modifica' },
        missing:   { dot: '#dc2626', badge: 'bg-red-100 text-red-600 border-red-200',        label: 'Mancante' },
    };
    // Legacy alias usato nella sezione print
    const sectionColors = {
        blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   button: 'bg-blue-600'   },
        purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', button: 'bg-purple-600' },
        amber:  { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700',  button: 'bg-amber-600'  },
        green:  { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-700',  button: 'bg-green-600'  },
        indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', button: 'bg-indigo-600' },
    };
    const statusColors = {
        confirmed: { bg: 'bg-green-50', border: 'border-green-300', badge: 'bg-green-100 text-green-700' },
        suggested: { bg: 'bg-amber-50', border: 'border-amber-300', badge: 'bg-amber-100 text-amber-700' },
        editing:   { bg: 'bg-blue-50',  border: 'border-blue-300',  badge: 'bg-blue-100 text-blue-700'   },
        missing:   { bg: 'bg-red-50',   border: 'border-red-300',   badge: 'bg-red-100 text-red-700'     },
    };

    const totalQuestions = QUESTIONNAIRE_STRUCTURE.reduce((s, sec) => s + sec.questions.length, 0);
    const confirmedCount = Object.values(confirmationStatus).filter(s => s === 'confirmed').length;
    const progressPct = totalQuestions > 0 ? Math.round((confirmedCount / totalQuestions) * 100) : 0;

    return (
        <div className="space-y-6">
            <style dangerouslySetInnerHTML={{ __html: `
                .print-section { display: none; }
                @media print {
                    body * { visibility: hidden; }
                    .print-section { display: block !important; visibility: visible !important; }
                    .print-section * { visibility: visible !important; }
                    .print-section { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%;
                        padding: 20px;
                        background: white !important;
                    }
                    .no-print { display: none !important; }
                    @page { margin: 20mm; }
                }
            ` }} />
            
            <div className="print-section">
                <div className="flex justify-between items-center border-b-2 border-indigo-600 pb-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-black text-indigo-950 uppercase">Report di Supervisione Professionale</h1>
                        <p className="text-sm text-indigo-600 font-bold uppercase tracking-widest mt-1">Modello BioGestalt • Riservato</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs font-bold text-slate-500 uppercase">Cliente</p>
                        <p className="text-lg font-black text-slate-800 uppercase">{client.name}</p>
                        <p className="text-[10px] text-slate-500 font-bold">{new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                    </div>
                </div>
                <div className="text-sm text-gray-950 leading-relaxed border-b border-gray-100 pb-6 mb-6">
                    <h2 className="text-[10px] font-black text-indigo-600 uppercase tracking-tighter mb-4">Sintesi Evolutiva Elaborata</h2>
                    {generateNarrativeSummary()}
                </div>

                {/* Sezioni di dettaglio per la supervisione */}
                <div className="space-y-8">
                    {QUESTIONNAIRE_STRUCTURE.map(section => {
                        const sectionAnswers = section.questions.filter(q => answers[q.id] && confirmationStatus[q.id] === 'confirmed');
                        if (sectionAnswers.length === 0) return null;

                        return (
                            <div key={section.id} className="break-inside-avoid">
                                <h3 className="text-xs font-black text-indigo-900 uppercase mb-3 flex items-center gap-2">
                                    <span className="opacity-50">{section.icon}</span> {section.title}
                                </h3>
                                <div className="grid grid-cols-1 gap-4">
                                    {sectionAnswers.map(q => (
                                        <div key={q.id} className="border-l-2 border-indigo-100 pl-4 py-0.5">
                                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-1">{q.label}</p>
                                            <p className="text-xs text-slate-900 leading-relaxed font-sans">{answers[q.id]}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="no-print space-y-5">

            {/* ── HEADER CARD (dark premium) ─────────────────────────────── */}
            <div style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%)' }}
                className="rounded-3xl p-7 shadow-xl">

                {/* Top row */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
                            style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
                            <span className="text-2xl">📋</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-black text-white">Questionario Guidato di Sintesi</h3>
                                <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest"
                                    style={{ background: 'rgba(255,255,255,0.15)', color: '#a5b4fc' }}>
                                    ✦ BioGestalt
                                </span>
                            </div>
                            <p className="text-[10px] text-indigo-300 font-semibold mt-0.5 uppercase tracking-widest">
                                Analisi AI · Revisione del counselor
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={handleGenerateAll}
                        disabled={isGenerating || (works.length === 0 && meetings.length === 0)}
                        className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 whitespace-nowrap ${
                            isGenerating
                                ? 'cursor-not-allowed opacity-50'
                                : 'hover:scale-105 active:scale-95'
                        }`}
                        style={isGenerating ? { background: 'rgba(255,255,255,0.1)', color: '#a5b4fc' }
                            : { background: 'linear-gradient(135deg, #818cf8, #6366f1)', color: '#fff', boxShadow: '0 4px 20px rgba(99,102,241,0.5)' }}
                    >
                        {isGenerating
                            ? <><span className="animate-spin">⚙️</span> Analizzando...</>
                            : <>✦ {Object.keys(answers).length > 0 ? 'Rigenera' : 'Genera Risposte'}</>}
                    </button>
                </div>

                {/* Progress row */}
                {Object.keys(answers).length > 0 && (
                    <div className="mt-5">
                        <div className="flex justify-between items-center mb-1.5">
                            <span className="text-[10px] text-indigo-300 font-bold uppercase tracking-widest">Completamento</span>
                            <span className="text-[10px] text-indigo-200 font-bold">{confirmedCount}/{totalQuestions} confermate · {progressPct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                            <div className="h-full rounded-full transition-all duration-700"
                                style={{ width: `${progressPct}%`, background: 'linear-gradient(90deg, #818cf8, #34d399)' }} />
                        </div>
                    </div>
                )}

                {/* Section chips */}
                <div className="mt-4 flex flex-wrap gap-2">
                    {QUESTIONNAIRE_STRUCTURE.map(sec => {
                        const secConfirmed = sec.questions.filter(q => confirmationStatus[q.id] === 'confirmed').length;
                        const allDone = secConfirmed === sec.questions.length;
                        return (
                            <button key={sec.id}
                                onClick={() => setIsExpanded(true)}
                                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold transition-all hover:scale-105"
                                style={allDone
                                    ? { background: 'rgba(52,211,153,0.2)', color: '#6ee7b7', border: '1px solid rgba(52,211,153,0.3)' }
                                    : { background: 'rgba(255,255,255,0.08)', color: '#a5b4fc', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <span>{sec.icon}</span>
                                <span>{sec.title.replace(/^.\s/, '')}</span>
                                {allDone && <span>✓</span>}
                            </button>
                        );
                    })}
                </div>
            </div>


            {/* ── SINTESI NARRATIVA COMPATTA ─────────────────────────────── */}
            {Object.keys(answers).length > 0 && !isExpanded && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">Sintesi del Percorso</h4>
                            <p className="text-[10px] text-slate-400 mt-0.5">
                                {confirmedCount}/{totalQuestions} risposte confermate
                                {lastUpdated && ` · ${new Date(lastUpdated).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}`}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={handlePrint}
                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-slate-500 border border-slate-200 hover:bg-slate-50 transition flex items-center gap-1">
                                🖨️ Stampa
                            </button>
                            <button onClick={handleExportPDF} disabled={isSaving}
                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-indigo-600 border border-indigo-200 hover:bg-indigo-50 transition flex items-center gap-1 disabled:opacity-50">
                                {isSaving ? '⏳ PDF...' : '📄 Scarica PDF'}
                            </button>
                            <button onClick={() => setIsExpanded(true)}
                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition flex items-center gap-1"
                                style={{ background: 'linear-gradient(135deg, #6366f1, #4338ca)' }}>
                                📋 Dettagli
                            </button>
                        </div>
                    </div>
                    <div className="px-6 py-5 space-y-3 text-sm text-slate-700 leading-relaxed">
                        {generateNarrativeSummary()}
                    </div>
                    {/* Highlights pills */}
                    <div className="px-6 pb-5 flex flex-wrap gap-2">
                        {answers.dominantEmotion && (
                            <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-purple-100 text-purple-700 border border-purple-200">
                                💭 {answers.dominantEmotion}
                            </span>
                        )}
                        {answers.characterStructure && (
                            <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">
                                🧠 {answers.characterStructure}
                            </span>
                        )}
                        {answers.centralTheme && (
                            <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                                📖 {answers.centralTheme}
                            </span>
                        )}
                    </div>
                </div>
            )}


            {/* ── CHIUDI DETTAGLI (solo in vista espansa) ─────────────────── */}
            {Object.keys(answers).length > 0 && isExpanded && (
                <div className="flex justify-end">
                    <button onClick={() => setIsExpanded(false)}
                        className="px-4 py-2 bg-slate-100 text-slate-600 text-xs font-bold rounded-xl hover:bg-slate-200 transition flex items-center gap-2">
                        ↑ Chiudi Dettagli
                    </button>
                </div>
            )}


            {/* Stato di generazione */}
            {isGenerating && (
                <div className="bg-white/60 backdrop-blur-sm p-12 rounded-2xl border border-indigo-50 flex flex-col items-center justify-center text-center space-y-4 animate-pulse">
                    <div className="flex gap-2">
                        <div className="w-3 h-3 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce"></div>
                    </div>
                    <p className="text-indigo-900 font-bold italic text-sm">
                        Sto analizzando diario, note di osservazione e baseline per compilare il questionario...
                    </p>
                </div>
            )}

            {/* ── SEZIONI DEL QUESTIONARIO (vista espansa) ─────────────── */}
            {!isGenerating && isExpanded && QUESTIONNAIRE_STRUCTURE.map((section) => {
                const style = sectionStyles[section.color];
                const secConfirmed = section.questions.filter(q => confirmationStatus[q.id] === 'confirmed').length;

                return (
                    <div key={section.id} className="bg-white rounded-2xl shadow-sm overflow-hidden"
                        style={{ borderLeft: `4px solid ${style.accent}` }}>
                        {/* Section header */}
                        <div className="flex items-center justify-between px-6 py-4"
                            style={{ background: style.light }}>
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">{section.icon}</span>
                                <h4 className="text-sm font-black uppercase tracking-tight"
                                    style={{ color: style.text }}>{section.title}</h4>
                            </div>
                            <span className="text-[10px] font-bold px-2 py-1 rounded-full"
                                style={{ background: `${style.accent}22`, color: style.text }}>
                                {secConfirmed}/{section.questions.length} ✓
                            </span>
                        </div>

                        {/* Questions */}
                        <div className="divide-y divide-slate-100">
                            {section.questions.map((question) => {
                                const answerStatus = getAnswerStatus(question.id);
                                const cfg = statusConfig[answerStatus.status];
                                const isEditingThis = isEditing[question.id];

                                return (
                                    <div key={question.id} className="px-6 py-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                                                {question.label}
                                            </label>
                                            <span className={`flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full border ${cfg.badge}`}>
                                                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: cfg.dot }} />
                                                {cfg.label}
                                            </span>
                                        </div>

                                        {isEditingThis ? (
                                            <div className="space-y-2">
                                                {question.type === 'textarea' ? (
                                                    <textarea value={answers[question.id] || ''}
                                                        onChange={(e) => handleEdit(question.id, e.target.value)}
                                                        placeholder={question.placeholder}
                                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-indigo-400 outline-none text-sm min-h-[90px] resize-none transition" />
                                                ) : (
                                                    <input type="text" value={answers[question.id] || ''}
                                                        onChange={(e) => handleEdit(question.id, e.target.value)}
                                                        placeholder={question.placeholder}
                                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-indigo-400 outline-none text-sm transition" />
                                                )}
                                                <div className="flex gap-2 justify-end">
                                                    <button onClick={() => setIsEditing({ ...isEditing, [question.id]: false })}
                                                        className="px-3 py-1.5 text-slate-500 text-xs font-bold hover:bg-slate-100 rounded-lg transition">Annulla</button>
                                                    <button onClick={() => handleSaveEdit(question.id)}
                                                        className="px-3 py-1.5 text-white text-xs font-bold rounded-lg transition"
                                                        style={{ background: style.accent }}>Salva</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                {answers[question.id] ? (
                                                    <p className="text-sm text-slate-700 leading-relaxed mb-2">{answers[question.id]}</p>
                                                ) : (
                                                    <p className="text-xs text-slate-400 italic mb-2">
                                                        {Object.keys(answers).length === 0
                                                            ? 'Clicca Genera Risposte per compilare automaticamente'
                                                            : 'Campo opzionale'}
                                                    </p>
                                                )}
                                                <div className="flex gap-2 justify-end">
                                                    {answerStatus.status !== 'confirmed' && answers[question.id] && (
                                                        <button onClick={() => handleConfirm(question.id)}
                                                            className="px-3 py-1 text-white text-[11px] font-bold rounded-lg transition"
                                                            style={{ background: '#16a34a' }}>✓ Conferma</button>
                                                    )}
                                                    <button onClick={() => setIsEditing({ ...isEditing, [question.id]: true })}
                                                        className="px-3 py-1 bg-slate-100 text-slate-600 text-[11px] font-bold rounded-lg hover:bg-slate-200 transition">✎ Modifica</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* ── EMPTY STATE ─────────────────────────────────────────── */}
            {!isGenerating && Object.keys(answers).length === 0 && (
                <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center flex flex-col items-center">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
                        style={{ background: 'linear-gradient(135deg,#eef2ff,#e0e7ff)' }}>
                        <span className="text-3xl">📋</span>
                    </div>
                    <p className="text-slate-800 font-black text-base mb-1">Questionario non ancora generato</p>
                    <p className="text-slate-400 text-sm mb-5 max-w-md leading-relaxed">
                        Clicca su <strong className="text-indigo-600">Genera Risposte</strong> in alto per avviare l'analisi automatica.
                        {(works.length === 0 && meetings.length === 0) && (
                            <span className="block mt-2 text-amber-600 font-semibold text-xs">
                                ⚠️ Aggiungi almeno un incontro o un lavoro per generare il questionario.
                            </span>
                        )}
                    </p>
                    {(works.length > 0 || meetings.length > 0) && (
                        <button onClick={handleGenerateAll}
                            className="px-7 py-2.5 text-white font-bold rounded-xl transition hover:scale-105 active:scale-95 text-sm"
                            style={{ background: 'linear-gradient(135deg, #818cf8, #4338ca)', boxShadow: '0 4px 20px rgba(99,102,241,0.35)' }}>
                            ✦ Genera Ora
                        </button>
                    )}
                </div>
            )}
            </div>{/* Chiusura no-print */}
        </div> // Chiusura container principale
    );
};

export default GuidedSynthesisQuestionnaire;
